import React, { useEffect, useState } from "react";
import { DollarSign, ExternalLink, Check, Loader2, AlertCircle, BarChart3 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

/**
 * Admin → Ad Manager tab.
 * Configure 4 programmatic ad platforms:
 *   • Google Ad Manager (+ AdX)
 *   • Amazon Publisher Services (TAM/DSP)
 *   • Magnite SSP (Rubicon)
 *   • The Trade Desk DSP
 * Each stores {enabled, priority, placements, config[]}. Backend composes
 * VAST tag URLs per request at GET /ads/vast and returns them to the player
 * which plays them via Google IMA SDK.
 *
 * Priority is the waterfall order (0 = try first). Placements toggle where
 * each provider can run (preroll / midroll / postroll).
 */
export default function AdManagerTab() {
  const [providers, setProviders] = useState([]);
  const [stats, setStats] = useState({ by_provider: {}, window_days: 30 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        api.get("/admin/ads/providers"),
        api.get("/admin/ads/stats").catch(() => ({ data: { by_provider: {}, window_days: 30 } })),
      ]);
      setProviders(p.data);
      setStats(s.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const update = async (providerKey, patch) => {
    setSaving(providerKey);
    try {
      const { data } = await api.put(`/admin/ads/providers/${providerKey}`, patch);
      setProviders((rows) => rows.map((r) => (r.provider === providerKey ? data : r)));
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(null); }
  };

  if (loading) return <div className="text-white/60">Loading ad providers…</div>;

  return (
    <div data-testid="ad-manager-tab" className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-bold">Ad Manager</div>
        <h2 className="font-display font-black text-4xl text-white tracking-tighter mt-1">Programmatic ad networks</h2>
        <p className="text-white/60 text-sm mt-2">
          Configure real publisher networks — GAM, AdX, Amazon, Magnite, The Trade Desk. The video player
          requests a VAST tag per impression; the highest-priority enabled provider wins the auction waterfall.
          You keep 100% of the ad revenue on your own ad accounts.
        </p>
      </div>

      {/* Stats row */}
      <section data-testid="ad-stats-row" className="grid sm:grid-cols-5 gap-3">
        <div className="rounded-xl border border-tv-accent/30 bg-tv-accent/5 p-4">
          <div className="text-[10px] uppercase tracking-wider text-white/50 font-bold flex items-center gap-1.5">
            <BarChart3 className="w-3 h-3" /> Last {stats.window_days}d (all)
          </div>
          <div className="font-display font-black text-2xl text-tv-accent mt-1">
            {Object.values(stats.by_provider).reduce((a, b) => a + (b.impression || 0), 0)}
          </div>
          <div className="text-xs text-white/50">impressions total</div>
        </div>
        {providers.map((p) => {
          const s = stats.by_provider[p.provider] || {};
          return (
            <div key={p.provider} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <div className="text-[10px] uppercase tracking-wider text-white/50 font-bold truncate">{p.provider}</div>
              <div className="font-display font-black text-2xl text-white mt-1">{s.impression || 0}</div>
              <div className="text-xs text-white/50">{s.click || 0} clicks · {s.error || 0} errors</div>
            </div>
          );
        })}
      </section>

      {/* Provider cards */}
      <section className="space-y-4">
        {providers.map((p) => (
          <ProviderCard
            key={p.provider}
            provider={p}
            saving={saving === p.provider}
            onChange={(patch) => update(p.provider, patch)}
          />
        ))}
      </section>
    </div>
  );
}

function ProviderCard({ provider, saving, onChange }) {
  const [localConfig, setLocalConfig] = useState(provider.config || {});
  const [localPlacements, setLocalPlacements] = useState(provider.placements || { preroll: true, midroll: false, postroll: false });
  const [localPriority, setLocalPriority] = useState(provider.priority ?? 100);
  const configured = provider.fields?.every((f) => !f.required || (localConfig[f.key] || "").toString().trim());

  const save = () => onChange({ config: localConfig, placements: localPlacements, priority: Number(localPriority) });
  const toggleEnabled = () => onChange({ enabled: !provider.enabled });

  return (
    <div
      data-testid={`ad-provider-${provider.provider}`}
      className={`tv-glass rounded-2xl border p-6 transition ${provider.enabled ? "border-tv-accent/40 bg-tv-accent/[0.02]" : "border-white/10"}`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-bold text-xl text-white">{provider.label}</h3>
            {provider.enabled ? (
              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300">Live</span>
            ) : (
              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-white/10 text-white/60">Disabled</span>
            )}
            {!configured && (
              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Needs config
              </span>
            )}
          </div>
          <p className="text-white/60 text-sm mt-1">{provider.description}</p>
          <a
            href={provider.docs_url}
            target="_blank"
            rel="noreferrer"
            data-testid={`ad-provider-docs-${provider.provider}`}
            className="text-tv-accent hover:underline text-xs mt-1 inline-flex items-center gap-1"
          >
            <ExternalLink className="w-3 h-3" /> Setup docs
          </a>
        </div>
        <button
          data-testid={`ad-provider-toggle-${provider.provider}`}
          onClick={toggleEnabled}
          disabled={!configured && !provider.enabled}
          className={`font-bold px-5 py-2 rounded-full text-sm flex items-center gap-2 transition ${
            provider.enabled
              ? "bg-red-500/15 text-red-300 hover:bg-red-500/25"
              : "bg-tv-accent text-black hover:bg-yellow-400 disabled:opacity-50"
          }`}
          title={!configured && !provider.enabled ? "Fill in required keys first" : ""}
        >
          {provider.enabled ? "Disable" : "Enable"}
        </button>
      </div>

      {/* Config grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
        {provider.fields?.map((f) => (
          <label key={f.key} className="block">
            <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold flex items-center gap-1">
              {f.label} {f.required && <span className="text-tv-accent">*</span>}
            </span>
            <input
              data-testid={`ad-provider-field-${provider.provider}-${f.key}`}
              value={localConfig[f.key] || ""}
              onChange={(e) => setLocalConfig({ ...localConfig, [f.key]: e.target.value })}
              placeholder={f.placeholder}
              className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent font-mono"
            />
          </label>
        ))}
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Priority (0 = first)</span>
          <input
            data-testid={`ad-provider-priority-${provider.provider}`}
            type="number"
            min="0"
            max="999"
            value={localPriority}
            onChange={(e) => setLocalPriority(e.target.value)}
            className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent"
          />
        </label>
      </div>

      {/* Placements */}
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold mr-1">Placements:</span>
        {["preroll", "midroll", "postroll"].map((slot) => (
          <label key={slot} className={`inline-flex items-center gap-1.5 text-xs bg-white/5 border border-white/10 rounded-full px-3 py-1 cursor-pointer ${localPlacements[slot] ? "text-tv-accent border-tv-accent/40" : "text-white/60"}`}>
            <input
              type="checkbox"
              data-testid={`ad-provider-placement-${provider.provider}-${slot}`}
              checked={!!localPlacements[slot]}
              onChange={(e) => setLocalPlacements({ ...localPlacements, [slot]: e.target.checked })}
              className="accent-tv-accent"
            />
            {slot}
          </label>
        ))}
      </div>

      <div className="mt-5">
        <button
          data-testid={`ad-provider-save-${provider.provider}`}
          onClick={save}
          disabled={saving}
          className="bg-tv-accent hover:bg-yellow-400 disabled:opacity-60 text-black font-bold px-5 py-2 rounded-full text-sm inline-flex items-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Save config
        </button>
      </div>
    </div>
  );
}
